import React, { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useContact, useGamesByIds, useMyProfile, usePublisherAndContacts } from "@seabrookstudios/pitch2table";
import { formatFullName, formatSessionDate } from "@seabrookstudios/pitch2table-core";
import { DateTime } from "luxon";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 * @param {string} props.meetingTimezone
 * @param {number} props.meetingDateMillis
 */
const PublisherLocalTimeRow = ({ publisherId, meetingTimezone, meetingDateMillis }) => {
  const { data: publisherAndContacts = [] } = usePublisherAndContacts(publisherId);
  const [publisher] = publisherAndContacts;

  const visible = Boolean(publisher?.timezone && publisher.timezone !== meetingTimezone);

  if (!visible || !publisher) {
    return null;
  }

  return (
    <Typography component="div" color="text.secondary">
      <Box component="span" fontWeight={600}>
        {publisher.name}
      </Box>
      {" - "}
      {formatSessionDate(meetingDateMillis, publisher.timezone)}
    </Typography>
  );
};

/**
 * Renders a row showing the meeting time in a designer's timezone.
 * Returns null if the designer is the current user, has no timezone, or is in the meeting timezone.
 * @param {object} props
 * @param {import('@seabrookstudios/types').UserId} props.designerId
 * @param {import('@seabrookstudios/types').UserId} props.myUserId
 * @param {number} props.dateMillis
 * @param {string} props.meetingTimezone
 */
const DesignerTimezoneRow = ({ designerId, myUserId, dateMillis, meetingTimezone }) => {
  const { data: contact } = useContact(designerId);

  if (!contact) {
    return null;
  }

  if (designerId === myUserId) {
    return null;
  }

  if (!contact.location?.timezone || contact.location.timezone === meetingTimezone) {
    return null;
  }

  return (
    <Typography component="div" color="text.secondary">
      <Box component="span" fontWeight={600}>
        {formatFullName(contact)}
      </Box>
      {" - "}
      {formatSessionDate(dateMillis, contact.location.timezone)}
    </Typography>
  );
};

/**
 * Ordered unique designer ids for session games (first occurrence wins in gameIds order).
 * @param {import("@seabrookstudios/types").GameId[]} gameIds
 * @param {import("@tanstack/react-query").UseQueryResult<import("@seabrookstudios/pitch2table-core").Game | null, Error>[]} gameResults
 * @returns {import("@seabrookstudios/types").UserId[]}
 */
const uniqueDesignerIdsForSessionGames = (gameIds, gameResults) => {
  const seen = new Set();
  const ordered = [];

  for (let i = 0; i < gameIds.length; i++) {
    const game = gameResults[i]?.data;
    if (!game?.designers) {
      continue;
    }
    for (const { designerId } of game.designers) {
      if (!seen.has(designerId)) {
        seen.add(designerId);
        ordered.push(designerId);
      }
    }
  }

  return ordered;
};

/**
 * Returns true if there is a daylight saving change between now and the meeting.
 * @param {number} dateMillis
 * @param {string} meetingTimezone
 * @returns {boolean}
 */
const useHasDaylightSavingChanges = (dateMillis, meetingTimezone) => {
  const { data: profile } = useMyProfile();
  if (!profile?.location) {
    return false;
  }

  const userNowInZone = DateTime.now().setZone(profile.location.timezone);
  const sessionInSessionZone = DateTime.fromMillis(dateMillis, { zone: meetingTimezone });

  if (userNowInZone.toMillis() > dateMillis) {
    // The meeting is in the past, so there is no daylight saving change.
    return false;
  }

  return userNowInZone.isInDST !== sessionInSessionZone.isInDST;
};

/**
 * Shows the meeting time translated into timezones that differ from the meeting timezone:
 * the signed-in user (when provided), linked publishers that have a known timezone, and
 * co-designers from the given games (each designer once across those games).
 *
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} props.publisherIds
 * @param {import("@seabrookstudios/types").GameId[]} props.gameIds
 * @param {string} props.meetingTimezone
 * @param {number} props.meetingDateMillis
 * @param {string | undefined | null} [props.viewerTimezone] e.g. profile.location.timezone
 * @param {string} [props.viewerLabel]
 */
export const SessionRelevantTimezones = ({
  publisherIds = [],
  gameIds = [],
  meetingTimezone,
  meetingDateMillis,
  viewerTimezone,
  viewerLabel = "You",
}) => {
  const { data: profile } = useMyProfile();
  const gameResults = useGamesByIds(gameIds);

  const hasDaylightSavingChanges = useHasDaylightSavingChanges(meetingDateMillis, meetingTimezone);

  const showViewer = Boolean(viewerTimezone && viewerTimezone !== meetingTimezone);

  const showAlert = showViewer || hasDaylightSavingChanges || publisherIds.length > 0 || gameIds.length > 0;

  const uniqueDesignerIds = useMemo(
    () => uniqueDesignerIdsForSessionGames(gameIds, gameResults),
    [gameIds, gameResults]
  );

  if (!showAlert) {
    return null;
  }

  if (!profile) {
    return null;
  }

  return (
    <>
      {hasDaylightSavingChanges && (
        <Alert severity="warning" icon={false}>
          There is a daylight saving change between now and the meeting. Double check the time.
        </Alert>
      )}
      <Alert severity="info" icon={false}>
        <Stack spacing={0.75}>
          {showViewer && (
            <Typography variant="body2" component="div" color="text.primary">
              <Box component="span" fontWeight={600}>
                {viewerLabel}
              </Box>
              {" - "}
              {formatSessionDate(meetingDateMillis, viewerTimezone ?? undefined)}
            </Typography>
          )}
          {uniqueDesignerIds.map((designerId) => (
            <DesignerTimezoneRow
              key={designerId}
              designerId={designerId}
              myUserId={profile.id}
              dateMillis={meetingDateMillis}
              meetingTimezone={meetingTimezone}
            />
          ))}
          {publisherIds.map((publisherId) => (
            <PublisherLocalTimeRow
              key={publisherId}
              publisherId={publisherId}
              meetingTimezone={meetingTimezone}
              meetingDateMillis={meetingDateMillis}
            />
          ))}
        </Stack>
      </Alert>
    </>
  );
};
